// useMinhasAvaliacoes.js — Avaliações do usuário logado (aba "Avaliações" do perfil).
//
// GET {API}minhas-avaliacoes   → { avaliacoes: [...], total }
// PUT {API}avaliacoes/{id}     → edita nota/comentário
//
// Item: { id, nota, comentario, aprovado, created_at, updated_at,
//         livro: { id, titulo, subtitulo, image } }
//
// Editar NÃO gera coins novos — o crédito é só na primeira publicação. Isso
// está escrito na tela também, senão vira reclamação.
import { useState, useEffect, useCallback } from "react";

const API_URL = process.env.REACT_APP_API;

const pegarToken = () =>
  localStorage.getItem("token" + (process.env.REACT_APP_USER_FUNCTION || ""));

const useMinhasAvaliacoes = () => {
  const [avaliacoes, setAvaliacoes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [erro, setErro] = useState(null);
  const [salvando, setSalvando] = useState(false);

  const carregar = useCallback(async () => {
    const token = pegarToken();
    if (!token) {
      setAvaliacoes([]);
      setLoading(false);
      return;
    }

    setLoading(true);
    setErro(null);
    try {
      const resp = await fetch(`${API_URL}minhas-avaliacoes`, {
        headers: { Accept: "application/json", Authorization: "Bearer " + token },
      });
      if (!resp.ok) throw new Error("Não foi possível carregar suas avaliações.");
      const d = await resp.json();
      setAvaliacoes(Array.isArray(d?.avaliacoes) ? d.avaliacoes : []);
    } catch (e) {
      setErro(e?.message || "Não foi possível carregar suas avaliações.");
      setAvaliacoes([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    carregar();
  }, [carregar]);

  /**
   * Salva a edição. Atualiza a lista em memória com o que o backend devolveu —
   * recarregar a lista inteira por causa de um campo faria a grade piscar.
   * Lança Error com a mensagem do backend (o modal mostra).
   */
  const salvar = useCallback(async (id, { nota, comentario }) => {
    setSalvando(true);
    try {
      const token = pegarToken();
      if (!token) throw new Error("Sua sessão expirou. Entre novamente.");

      const resp = await fetch(`${API_URL}avaliacoes/${id}`, {
        method: "PUT",
        headers: {
          "Content-Type": "application/json",
          Accept: "application/json",
          Authorization: "Bearer " + token,
        },
        body: JSON.stringify({ nota, comentario }),
      });

      if (!resp.ok) {
        let msg = "Não foi possível salvar a alteração.";
        if (resp.status === 401) msg = "Sua sessão expirou. Entre novamente.";
        else if (resp.status === 429) msg = "Muitas alterações seguidas. Aguarde um minuto.";
        else {
          try {
            const erroJson = await resp.json();
            msg = erroJson?.message || erroJson?.error || msg;
          } catch { /* resposta sem JSON → mensagem padrão */ }
        }
        throw new Error(msg);
      }

      const d = await resp.json();
      const atualizada = d?.avaliacao;
      setAvaliacoes((lista) =>
        lista.map((a) =>
          a.id === id
            ? { ...a, nota: atualizada?.nota ?? nota, comentario: atualizada?.comentario ?? comentario, updated_at: atualizada?.updated_at ?? a.updated_at }
            : a
        )
      );
      return true;
    } finally {
      setSalvando(false);
    }
  }, []);

  return { avaliacoes, total: avaliacoes.length, loading, erro, salvando, salvar, recarregar: carregar };
};

export default useMinhasAvaliacoes;
